/**
 * MotifMenuOptions.js
 * Created by calhosh on 7/6/2021.
 */
import React, {useMemo, useCallback} from 'react';
import {useSelector} from 'react-redux';
import {Link} from 'react-router-dom';
import styled from 'styled-components';
import {labels} from '../../../util/utils';
import env from '../../../util/env';
import {documentTypes, smartScreenRoutes, gaRoleTypes} from '../../../util/uiconstants';
import EllipseControl from '../../Common/EllipsesControl/EllipsesControl';
import MenuItemGroup from '@ey/motif-react-wrapper/MenuItemGroup';
import MenuItem from '@ey/motif-react-wrapper/MenuItem';
import {useIsDrawerMode} from '../../../util/Ares/customHooks';
import {useQualtrics} from '@ey/canvascoreservices/Qualtrics.js';

export default function MotifMenuOptions(props) {
	const {
		baseCanvasUrl,
		engagementId,
		engagementDescription,
		featureConfigurations,
		gaRoleTypeId,
		quickLinks,
		engagement,
		isOfflineMode
	} = props;

	const isAresView = useSelector((store) => store.isAresView),
		configurations = useSelector((store) => store.configurations);

	const configFeatures = configurations && configurations?.features && configurations?.features;
	const isDrawerMode = useIsDrawerMode();

	const projectId = configFeatures
		? configFeatures.qualtrics
			? isAresView
				? configFeatures.qualtrics.aresQualtricsProjectId
				: configFeatures.qualtrics.kronosQualtricsProjectId
			: undefined
		: undefined;

	let showQualtricsLink =
		qualtricsInterceptAPI &&
		projectId &&
		!isDrawerMode &&
		(isAresView
			? configFeatures?.qualtrics?.isAresQualtricsEnabled
			: configFeatures?.qualtrics?.isKronosQualtricsEnabled === true);

	const qualtricsInterceptAPI = useQualtrics(projectId);

	const onLeaveUsFeedbackClick = (e) => {
		qualtricsInterceptAPI((API) => {
			API.unload();
			API.load().done(runQualtricsIntercept);
		});
		e.preventDefault();
	};

	const runQualtricsIntercept = () => {
		qualtricsInterceptAPI((API) => {
			API.run();
		});
	};

	const renderQualtricsMotifLinkOption = useCallback((title) => {
		return (
			<MenuItem
				onClick={() => {
					onOptionClick();
				}}
			>
				<Link to={'/'} onClick={onLeaveUsFeedbackClick}>
					<EllipseControl content={title} tooltip={title} noOfLines={1} />
				</Link>
			</MenuItem>
		);
	}, []);

	const onOptionClick = useCallback(() => {
		window.dispatchEvent(new CustomEvent('closeMenu', {}));
	}, []);

	const isActiveSection = useCallback((elevatedSection) => {
		let pathname = window.location.pathname.substr(1);
		let firstSlashIndex = pathname.indexOf('/');
		let activeSection = firstSlashIndex > -1 ? pathname.substr(0, firstSlashIndex) : pathname;
		if (activeSection === elevatedSection) {
			return 'selected';
		}
	}, []);

	const itemTitle = useCallback((label) => {
		return label;
	}, []);

	const renderMotifLinkOption = useCallback((href, title, onClick) => {
		return (
			<MenuItem
				onClick={() => {
					onOptionClick();
					onClick && onClick();
				}}
			>
				<Link to={href} title={title} isEllipsed label={title} />
			</MenuItem>
		);
	}, []);

	const renderExternalLinkOption = useCallback((href, title, className, onClick, target) => {
		return (
			<MenuItem
				onClick={() => {
					onOptionClick();
					onClick && onClick();
				}}
			>
				<Link to={href} className={className} target={target ? target : '_blank'}>
					<EllipseControl content={title} tooltip={title} noOfLines={1} />
				</Link>
			</MenuItem>
		);
	}, []);

	const groupsConfig = useMemo(() => {
		const kronosBaseUrl = env.getURL('kronos_web');

		const showALRADocumentLink = quickLinks.accountLevelRiskAssessmentDocumentId !== null,
			showUnderstandITProcessLink = quickLinks.itFlowISA315DocumentId !== null;

		const alraDocumentUrl =
				showALRADocumentLink &&
				`fit?engagementid=${engagementId}&documentid=${quickLinks.accountLevelRiskAssessmentDocumentId}`,
			understandITProcessDocUrl =
				showUnderstandITProcessLink &&
				`fit?engagementid=${engagementId}&documentid=${quickLinks.itFlowISA315DocumentId}`;

		let alraSummaryUrl = kronosBaseUrl + alraDocumentUrl,
			understandITProcessUrl = kronosBaseUrl + understandITProcessDocUrl;

		if (kronosBaseUrl && !kronosBaseUrl.endsWith('/')) {
			alraSummaryUrl = `${kronosBaseUrl}/${alraDocumentUrl}`;
			understandITProcessUrl = `${kronosBaseUrl}/${understandITProcessDocUrl}`;
		}

		const showGroupStructureLink = quickLinks.hasGroupStructureSummary;
		const groupStructureUrl = quickLinks.groupStructureV2DocumentId
			? `${kronosBaseUrl}fit?engagementid=${engagementId}&documentid=${quickLinks.groupStructureV2DocumentId}`
			: baseCanvasUrl + '#groupauditstructuresummary';

		const showGroupInstructionLink = quickLinks.hasGroupInstructionSummary;
		let groupInstructionUrl = quickLinks.groupInstructionV2DocumentId
			? `${kronosBaseUrl}fit?engagementid=${engagementId}&documentid=${quickLinks.groupInstructionV2DocumentId}`
			: baseCanvasUrl + '#groupauditinstructionsummary';

		const showGroupInvolvementLink = quickLinks.groupInvolvementV2DocumentId !== null;
		let groupInvolvementUrl = quickLinks.groupInvolvementV2DocumentId
			? `${kronosBaseUrl}/fit?engagementid=${engagementId}&documentid=${quickLinks.groupInvolvementV2DocumentId}`
			: baseCanvasUrl + '#groupauditinstructionsummary';

		return [
			{
				name: itemTitle(labels.scopeAndStrategy),
				openButtonProps: labels.scopeAndStrategy,
				disabled: isOfflineMode,
				options: [
					{
						show: quickLinks.hasAccountSummary && quickLinks.accountLevelRiskAssessmentDocumentId === null,
						render: () => renderExternalLinkOption(baseCanvasUrl + '#accountsummary', labels.accountSummary)
					},
					{
						show: showALRADocumentLink,
						render: () =>
							renderExternalLinkOption(
								alraSummaryUrl,
								itemTitle(labels.accountRiskAssessmentSummary),
								undefined,
								undefined,
								'_self'
							)
					},
					{
						show: quickLinks.hasScotSummary,
						render: () => renderExternalLinkOption(baseCanvasUrl + '#scotsummary', labels.scotSummary)
					},
					{
						show: quickLinks.hasCRASummary,
						render: () => renderExternalLinkOption(baseCanvasUrl + '#crasummary', labels.navCRASummary)
					},
					{
						show: quickLinks.hasITFlowISA315,
						render: () => renderExternalLinkOption(understandITProcessUrl, labels.understandITProcess)
					},
					{
						show: quickLinks.hasItSummary,
						render: () => renderExternalLinkOption(baseCanvasUrl + '#itsummary', labels.navItSummary)
					},
					{
						show: quickLinks.hasSubstantiveEvaluationMatrix,
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') +
									`${smartScreenRoutes.sem}?engagementId=${engagementId}&documentid=${quickLinks &&
										quickLinks.semDocumentId}`,
								itemTitle(labels.navSEM),
								isActiveSection(smartScreenRoutes.sem),
								undefined,
								'_self'
							),
						title: itemTitle(labels.navSEM)
					},
					{
						show: quickLinks.hasAuditPlanSummary,
						render: () => renderExternalLinkOption(baseCanvasUrl + '#plan', labels.navAuditPlan)
					},
					{
						show: quickLinks.hasWorkPlanSummary,
						render: () =>
							renderExternalLinkOption(baseCanvasUrl + '#plan', labels.navWorkPlan, undefined, undefined, '_self')
					}
				]
			},
			{
				name: itemTitle(labels.navConclusion),
				openButtonProps: labels.navConclusion,
				disabled: isOfflineMode,
				options: [
					{
						show: quickLinks.hasAuditPlanSummary,
						render: () => renderExternalLinkOption(baseCanvasUrl + '#findingsHub', labels.navFindings)
					},
					{
						show: quickLinks.hasModificationsAfterRRD && quickLinks.modifiedAfterRRDDocumentId,
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') +
									`/smartscreens/440gl?engagementid=${engagementId}&documentid=${quickLinks.modifiedAfterRRDDocumentId}`,
								itemTitle(labels.nav440GL),
								isActiveSection(smartScreenRoutes.fourFortyGl),
								undefined,
								'_self'
							)
					},
					{
						show: quickLinks.hasIndependence,
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') +
									'/smartscreens/independence?engagementid=' +
									engagementId +
									'&documentid=' +
									quickLinks.independenceSummaryDocId,
								itemTitle(labels.navTeamMemberIndependence),
								isActiveSection(smartScreenRoutes.independence),
								undefined,
								'_self'
							)
					},
					{
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') + '/archivetimeline?engagementid=' + engagementId,
								itemTitle(labels.navArchiveChecklist),
								undefined,
								undefined,
								'_self'
							)
					}
				]
			},
			{
				name: itemTitle(labels.navGroupAudit),
				openButtonProps: labels.navGroupAudit,
				disabled: isOfflineMode,
				show: quickLinks.hasGroupInstructionSummary || quickLinks.hasGroupStructureSummary || gaRoleTypeId,
				options: [
					{
						show: showGroupStructureLink,
						render: () =>
							renderExternalLinkOption(
								groupStructureUrl,
								labels.navGroupStructureSummary,
								undefined,
								undefined,
								'_self'
							)
					},
					{
						show: showGroupInstructionLink,
						render: () =>
							renderExternalLinkOption(
								groupInstructionUrl,
								labels.navGroupInstructionSummary,
								undefined,
								undefined,
								'_self'
							)
					},
					{
						show: showGroupInvolvementLink,
						render: () =>
							renderExternalLinkOption(groupInvolvementUrl, labels.navGroupInvolvement, undefined, undefined, '_blank')
					},
					{
						show:
							(gaRoleTypeId === gaRoleTypes.primary || gaRoleTypeId === gaRoleTypes.component) &&
							engagement.isGroupAuditEngagement,
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') + '/dashboard?engagementId=' + engagementId + '#groupStatus',
								labels.navGroupStatus,
								undefined
							)
					},
					{
						show: gaRoleTypeId === gaRoleTypes.regional,
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') + '/dashboard?engagementId=' + engagementId + '#primaryGroupStatus',
								labels.navPrimaryStatus,
								undefined
							)
					},
					{
						show: gaRoleTypeId === gaRoleTypes.regional,
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') + '/dashboard?engagementId=' + engagementId + '#componentGroupStatus',
								labels.navComponentStatus,
								undefined
							)
					},
					{
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') + '/dashboard?engagementId=' + engagementId + '#activityFeed',
								labels.navGroupActivityFeed,
								undefined
							)
					}
				]
			},
			{
				name: itemTitle(labels.navEngagementManagement),
				openButtonProps: labels.navEngagementManagement,
				disabled: isOfflineMode,
				options: [
					{
						show: quickLinks.hasV2Profile,
						render: () =>
							renderExternalLinkOption(
								'/fit?engagementid=' + engagementId + '&documentid=' + quickLinks.profileV2DocumentId,
								labels.navProfile
							)
					},
					{
						show: quickLinks.hasV2Profile === false && quickLinks.hasV1Profile,
						render: () =>
							renderExternalLinkOption(
								baseCanvasUrl +
									`&engagementdesc=${encodeURIComponent(engagementDescription)}#engagementprofile/${
										quickLinks.profileV1DocumentId
									}/${documentTypes.PROFILE}&referrer=${encodeURI(window.location.href)}`,
								labels.navProfile
							)
					},
					{
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') + '/manageteam?engagementid=' + engagementId,
								itemTitle(labels.navHeaderManageTeam),
								isActiveSection(smartScreenRoutes.independence),
								undefined,
								'_self'
							)
					},
					{
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') + '/contentupdates?engagementid=' + engagementId,
								itemTitle(labels.navContentUpdates),
								undefined,
								undefined,
								'_self'
							)
					},
					{
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') + '/copyhub?engagementid=' + engagementId,
								itemTitle(labels.navCopyHub),
								undefined,
								undefined,
								'_self'
							)
					},
					{
						show: featureConfigurations?.features?.enableHerculesReports === false,
						render: () =>
							renderExternalLinkOption(
								env.getURL('Zeus_web') + `/reportingHub?engagementid=${engagementId}`,
								itemTitle(labels.navReporting)
							)
					},
					{
						show: featureConfigurations?.features?.enableHerculesReports === true,
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') + '/reports' + `?engagementid=${engagementId}`,
								itemTitle(labels.navReporting)
							)
					}
				]
			}
		];
	}, [isOfflineMode, quickLinks, gaRoleTypeId, engagement, engagementId, engagementDescription]);

	const optionsConfig = useMemo(() => {
		return [
			{
				show: featureConfigurations?.features?.hasHelixProjects,
				render: () =>
					renderExternalLinkOption(
						env.getURL('hercules_web') + `/connections?engagementId=${engagementId}`,
						labels.navHelixProjects,
						undefined,
						undefined,
						'_self'
					),
				disabled: isOfflineMode
			},
			{
				show: featureConfigurations?.features?.canvasEconomicsBase,
				render: () =>
					renderExternalLinkOption(featureConfigurations.features.canvasEconomicsBase, labels.navCanvasEconomics),
				disabled: isOfflineMode
			},
			{
				show: featureConfigurations?.features?.eyOversightBase,
				render: () => renderExternalLinkOption(featureConfigurations.features.eyOversightBase, labels.navOversight),
				disabled: isOfflineMode
			},
			{
				render: () => renderExternalLinkOption(env.getURL('help_web'), itemTitle(labels.navHelp)),
				title: itemTitle(labels.navHelp),
				disabled: isOfflineMode
			},
			{
				render: () => renderExternalLinkOption(env.getURL('validation_help_web'), itemTitle(labels.validationNavHelp)),
				disabled: isOfflineMode
			},
			{
				show: showQualtricsLink,
				render: () => renderQualtricsMotifLinkOption(itemTitle(labels.leaveUsFeedback))
			}
		];
	}, [quickLinks, featureConfigurations, engagementId, isOfflineMode]);

	const renderGroup = useCallback((group) => {
		if (group.disabled) {
			return <StyledDisabledItem>{group.name}</StyledDisabledItem>;
		} else {
			let options = group.options.filter((option) => {
				return option.show === undefined || option.show;
			});

			return (
				<MenuItemGroup groupName={group.name} openButtonProps={group.openButtonProps}>
					{options?.length > 0 ? (
						<React.Fragment>
							{options.map((option) => {
								return renderOption(option);
							})}
						</React.Fragment>
					) : (
						<section className="motif-menu-list-item NotApplicableItem">
							<EllipseControl content={labels.navNotApplicable} tooltip={labels.navNotApplicable} noOfLines={1} />
						</section>
					)}
				</MenuItemGroup>
			);
		}
	}, []);

	const renderOption = useCallback((option) => {
		if (option.disabled) {
			return <StyledDisabledItem>{option.title}</StyledDisabledItem>;
		} else {
			if (option.href) {
				return (
					<MenuItem
						onClick={() => {
							onOptionClick();
							option.onClick && option.onClick();
						}}
					>
						<Link
							data-opennewtab
							to={option.href}
							className={option.className}
							target="_blank"
							rel="noopener noreferrer"
						>
							<EllipseControl content={option.title} tooltip={option.title} noOfLines={1} />
						</Link>
					</MenuItem>
				);
			} else {
				return option.render && option.render();
			}
		}
	}, []);

	return (
		<React.Fragment>
			{groupsConfig.map((group) => {
				if (group.show === undefined || group.show) {
					return renderGroup(group);
				}
			})}
			{optionsConfig.map((option) => {
				if (option.show === undefined || option.show) {
					return renderOption(option);
				}
			})}
		</React.Fragment>
	);
}

const StyledDisabledItem = styled.section`
	opacity: 0.5;
	pointer-events: none;
	height: var(--px-45);
	font-size: var(--px-16);
	padding: var(--px-15) 0 0 var(--px-19);
`;
